import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveContent } from '../ui/ResponsiveLayout';
import BrandSymbol from '../ui/BrandSymbol';
import NavigationFooter from '../ui/NavigationFooter';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import CSVContactImport from '../ui/CSVContactImport';
import type { UnifiedContact } from '../../services/unifiedContactService';
import { unifiedContactService } from '../../services/unifiedContactService';
import { RELATIONSHIP_OPTIONS } from '../../constants/relationshipOptions';

// Legacy Contact interface for CSV import component compatibility
interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  relationship: string;
  notes?: string;
}

const Frame155: React.FC = () => {
  const navigate = useNavigate();
  const { syncContactsFromBackend } = useSessionPersistence();
  const [contacts, setContacts] = useState<UnifiedContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load contacts from backend on mount
  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const allContacts = await unifiedContactService.getAllContacts();
      setContacts(allContacts);
    } catch (err) {
      console.error('Error loading contacts:', err);
      setError('Failed to load contacts');
    }
  };

  const validateContact = (contact: Contact): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Required fields
    if (!contact.firstName?.trim()) errors.push('First Name is required');
    if (!contact.lastName?.trim()) errors.push('Last Name is required');
    if (!contact.relationship?.trim()) errors.push('Relationship is required');

    // At least one contact method required
    if (!contact.email?.trim() && !contact.phone?.trim()) {
      errors.push('Either Email or Phone is required');
    }

    // Email format validation
    if (contact.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim())) {
      errors.push('Invalid email format');
    }

    // Phone format validation
    if (contact.phone?.trim() && !/^\+?[\d\s-()]{10,}$/.test(contact.phone.trim())) {
      errors.push('Invalid phone format');
    }

    // Relationship validation
    const validRelationships = RELATIONSHIP_OPTIONS.map(r => r.toLowerCase());
    if (!validRelationships.includes(contact.relationship?.toLowerCase().trim())) {
      errors.push('Invalid relationship type');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const handleAddContact = async (contact: Contact) => {
    setLoading(true);
    setError(null);
    try {
      // Validate contact
      const validation = validateContact(contact);
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        return;
      }

      // Create contact via unified service
      const newContact = await unifiedContactService.createContact({
        firstName: contact.firstName.trim(),
        lastName: contact.lastName.trim(),
        email: contact.email?.trim() || '',
        phone: contact.phone?.trim(),
        relationship: contact.relationship.toLowerCase().trim(),
        source: 'manual',
        notes: contact.notes?.trim()
      });

      // Add to local state
      setContacts(prev => [...prev, newContact]);

      // Sync to session
      await syncContactsFromBackend();

      // Clear any error
      setError(null);
    } catch (err: unknown) {
      if (err.code === 'DUPLICATE_CONTACT') {
        setError(`A contact with email ${contact.email} already exists`);
      } else {
        setError('Failed to add contact');
      }
      console.error('Error adding contact:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImportContacts = async (importedContacts: Contact[]) => {
    setLoading(true);
    setError(null);
    try {
      // Convert to unified format
      const contactsToImport = importedContacts.map(c => ({
        firstName: c.firstName.trim(),
        lastName: c.lastName.trim(),
        email: c.email?.trim() || '',
        phone: c.phone?.trim(),
        relationship: c.relationship?.toLowerCase().trim(),
        notes: c.notes?.trim()
      }));

      // Bulk import via unified service
      const result = await unifiedContactService.bulkImport(contactsToImport, 'csv');

      // Update local state
      setContacts(prev => [...prev, ...result.contacts]);

      // Sync to session
      await syncContactsFromBackend();

      // Show results
      if (result.duplicates > 0 || result.errors > 0) {
        const messages = [];
        if (result.imported > 0) messages.push(`${result.imported} contacts imported`);
        if (result.duplicates > 0) messages.push(`${result.duplicates} duplicates skipped`);
        if (result.errors > 0) messages.push(`${result.errors} errors encountered`);
        setError(messages.join(', '));
      } else {
        setError(null);
      }
    } catch (err) {
      setError('Failed to import contacts');
      console.error('Error importing contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    try {
      await unifiedContactService.deleteContact(contactId);

      // Update local state
      setContacts(prev => prev.filter(c => c.id !== contactId));

      // Sync to session
      await syncContactsFromBackend();
    } catch (err) {
      setError('Failed to delete contact');
      console.error('Error deleting contact:', err);
    }
  };

  const handleNext = () => {
    navigate('/frame-138');
  };

  const handleBack = () => {
    navigate('/frame-137');
  };

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        {/* Centered Logo */}
        <div className="flex justify-center mb-8">
          <BrandSymbol size="3xl" variant="dark" showText={true} />
        </div>

        {/* Main Content */}
        <div className="max-w-3xl mx-auto">
          <h1 className="text-2xl font-semibold text-stone-800 mb-4">
            Add Contacts Manually
          </h1>
          <p className="text-stone-600 mb-8">
            Enter your contacts' information below. These contacts will be available throughout your estate plan for executors, beneficiaries, and memories.
          </p>

          {/* Contact List */}
          <div className="space-y-4 mb-8">
            {contacts.map(contact => (
              <div
                key={contact.id}
                className="bg-white rounded-lg shadow-sm p-4 flex justify-between items-center"
              >
                <div>
                  <h3 className="font-medium text-stone-800">
                    {contact.firstName} {contact.lastName}
                  </h3>
                  {contact.relationship && (
                    <p className="text-sm text-stone-600 capitalize">
                      {contact.relationship}
                    </p>
                  )}
                  <p className="text-sm text-stone-500">
                    {contact.email && <span className="mr-3">{contact.email}</span>}
                    {contact.phone && <span>{contact.phone}</span>}
                  </p>
                  {contact.source && (
                    <span className="text-xs text-stone-400 capitalize">
                      Source: {contact.source}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleDeleteContact(contact.id)}
                  className="text-red-600 hover:text-red-700"
                  title="Delete contact"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

          {/* CSV Import */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <CSVContactImport
              onImport={handleImportContacts}
              onError={setError}
            />
          </div>

          {/* Add Contact Form */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-stone-800 mb-4">Add New Contact</h2>
            <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const newContact: Contact = {
                  id: '', // Will be generated by backend
                  firstName: formData.get('firstName') as string,
                  lastName: formData.get('lastName') as string,
                  email: formData.get('email') as string,
                  phone: formData.get('phone') as string,
                  relationship: formData.get('relationship') as string,
                  notes: formData.get('notes') as string
                };
                handleAddContact(newContact);
                (e.target as HTMLFormElement).reset();
              }} className="space-y-4">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    required
                    className="w-full px-3 py-2 border border-stone-300 rounded-md"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    required
                    className="w-full px-3 py-2 border border-stone-300 rounded-md"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    className="w-full px-3 py-2 border border-stone-300 rounded-md"
                    disabled={loading}
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    className="w-full px-3 py-2 border border-stone-300 rounded-md"
                    disabled={loading}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              {/* Relationship */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Relationship *
                </label>
                <select
                  name="relationship"
                  required
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  disabled={loading}
                >
                  <option value="">Select relationship</option>
                  {RELATIONSHIP_OPTIONS.map(r => (
                    <option key={r} value={r.toLowerCase()}>{r}</option>
                  ))}
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Notes
                </label>
                <textarea
                  name="notes"
                  rows={3}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  disabled={loading}
                  placeholder="Add any additional information..."
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  className={`px-4 py-2 text-white rounded-md ${
                    loading ? 'bg-cyan-500 cursor-not-allowed' : 'bg-cyan-700 hover:bg-cyan-800'
                  }`}
                  disabled={loading}
                >
                  {loading ? 'Adding...' : 'Add Contact'}
                </button>
              </div>
            </form>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-md">
              {error}
            </div>
          )}
        </div>
      </ResponsiveContent>

      {/* Navigation Footer */}
      <NavigationFooter
        variant="fixed"
        backRoute="/frame-137"
        nextRoute="/frame-138"
        progressTitle="About you and your loved ones"
        stepText="3-step questionnaire, approximately 5 minutes."
        progressPercentage={66}
        onNext={handleNext}
        onBack={handleBack}
      />
    </ResponsiveContainer>
  );
};

export default Frame155;
